import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { updateUserCommunityRole } from '@/api/accounts';

const schema = zod.object({
  role_name: zod.string().min(1, { message: 'Role is required' }),
});

type Values = zod.infer<typeof schema>;

const communityRoles = [
  { value: 'community_manager', label: 'Community Manager' },
  { value: 'board_member', label: 'Board Member' },
  { value: 'community_member', label: 'Community Member' },
];

interface EditCommunityRoleModalProps {
  accountId: string;
  userId: string;
  communityId: string;
  communityName: string;
  currentRole: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditCommunityRoleModal({
  accountId,
  userId,
  communityId,
  communityName,
  currentRole,
  open,
  onClose,
  onSuccess,
}: EditCommunityRoleModalProps): React.JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { role_name: currentRole },
  });

  React.useEffect(() => {
    if (open) {
      reset({ role_name: currentRole });
    }
  }, [open, currentRole, reset]);

  const mutation = useMutation({
    mutationFn: (data: Values) => updateUserCommunityRole(accountId, userId, communityId, data),
    onSuccess: () => {
      toast.success('Community role updated');
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update community role');
    },
  });

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={open}
      PaperProps={{ component: 'form', onSubmit: handleSubmit((v) => mutation.mutate(v)) }}
    >
      <DialogTitle>Change Community Role</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          Updating role in <strong>{communityName}</strong>
        </Typography>
        <Controller
          control={control}
          name="role_name"
          render={({ field }) => (
            <FormControl error={Boolean(errors.role_name)} fullWidth>
              <InputLabel>Community Role</InputLabel>
              <Select {...field} label="Community Role">
                {communityRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.role_name && <FormHelperText>{errors.role_name.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={mutation.isPending} type="submit" variant="contained">
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
